import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip } from '../../components/ui/chart';


const CustomTooltipContent = ({ active, payload, label, config }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="rounded-lg border p-3 text-sm shadow-md"
        style={{
          background: 'var(--q3-surface-default)',
          borderColor: 'var(--q3-stroke-normal)',
          minWidth: '200px',
        }}
      >
        <div className="font-bold mb-2" style={{ color: 'var(--q3-neutral-default)' }}>
          {label}
        </div>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={`item-${index}`} className="flex items-center justify-between gap-x-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                <span style={{ color: 'var(--q3-neutral-light)' }}>{config?.[entry.name]?.label || entry.name}</span>
              </div>
              <span className="font-bold" style={{ color: 'var(--q3-neutral-default)' }}>
                {entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const CustomXAxisTick = ({ x, y, payload, index, hoveredIndex, data }) => {
  const text = payload.value;
  const maxCharsPerLine = 15;
  const lineHeight = 12;
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  // Word wrapping
  words.forEach(word => {
    if ((currentLine + ' ' + word).length > maxCharsPerLine && currentLine.length > 0) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = currentLine.length === 0 ? word : currentLine + ' ' + word;
    }
  });
  lines.push(currentLine);

  const middleIndex = Math.floor(data.length / 2);

  // Control spacing of ellipses
  const ellipsisOffset = 100; // adjust this for bigger gap

  // First tick
  if (index === 0) {
    return (
      <g transform={`translate(${x},${y})`}>
        {/* Label rotated */}
        <text
          dy={16}
          textAnchor="end"
          fill={hoveredIndex === index ? 'var(--q3-accent-normal)' : 'var(--q3-neutral-light)'}
          fontSize={10}
          fontWeight={hoveredIndex === index ? 'bold' : 'normal'}
          transform="rotate(-45)"
        >
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight}>
              {line}
            </tspan>
          ))}
        </text>

        {/* Horizontal dots */}
        <text
          dy={16}
          x={ellipsisOffset}
          textAnchor="middle"
          fill="var(--q3-neutral-light)"
          fontSize={12}
        >
          ...
        </text>
      </g>
    );
  }

  // Middle tick
  if (index === middleIndex) {
    return (
      <g transform={`translate(${x},${y})`}>
        {/* Left dots */}
        <text
          dy={16}
          x={-ellipsisOffset}
          textAnchor="middle"
          fill="var(--q3-neutral-light)"
          fontSize={12}
        >
          ...
        </text>

        {/* Label rotated */}
        <text
          dy={16}
          textAnchor="end"
          fill={hoveredIndex === index ? 'var(--q3-accent-normal)' : 'var(--q3-neutral-light)'}
          fontSize={10}
          fontWeight={hoveredIndex === index ? 'bold' : 'normal'}
          transform="rotate(-45)"
        >
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight}>
              {line}
            </tspan>
          ))}
        </text>

        {/* Right dots */}
        <text
          dy={16}
          x={ellipsisOffset}
          textAnchor="middle"
          fill="var(--q3-neutral-light)"
          fontSize={12}
        >
          ...
        </text>
      </g>
    );
  }

  // Last tick
  if (index === data.length - 1) {
    return (
      <g transform={`translate(${x},${y})`}>
        {/* Left dots */}
        <text
          dy={16}
          x={-ellipsisOffset}
          textAnchor="middle"
          fill="var(--q3-neutral-light)"
          fontSize={12}
        >
          ...
        </text>

        {/* Label rotated */}
        <text
          dy={16}
          textAnchor="end"
          fill={hoveredIndex === index ? 'var(--q3-accent-normal)' : 'var(--q3-neutral-light)'}
          fontSize={10}
          fontWeight={hoveredIndex === index ? 'bold' : 'normal'}
          transform="rotate(-45)"
        >
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  }

  // Skip all others
  return null;
};



const MistakesLineChart = ({ title, description, data, expanded, onToggleExpand, lines, xAxisDataKey }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredLine, setHoveredLine] = useState(null);

  const chartConfig = lines.reduce((acc, line) => {
    acc[line.dataKey] = { label: line.label, color: line.color };
    return acc;
  }, {});

  const processedData = useMemo(() => {
    const shortenLabel = (label) => {
      if (typeof label !== 'string' || !label.includes('Test')) {
        return label;
      }
      const acronymMatch = label.match(/\(([^)]+)\)/);
      const numberMatch = label.match(/-\s*(\d+)$/);

      if (acronymMatch && numberMatch) {
        return `${acronymMatch[1]}-${numberMatch[1]}`;
      }
      return label;
    };

    return data.map((item) => ({
      ...item,
      [xAxisDataKey]: shortenLabel(item[xAxisDataKey]),
    }));
  }, [data, xAxisDataKey]);

  return (
    <Card className="mb-6 py-3 gap-2 shadow-none">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[var(--q3-neutral-default)]">{title}</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleExpand}
          className="rounded-full border text-[var(--q3-accent-normal)]"
        >
          {expanded ? 'HIDE' : 'VIEW'}
          {expanded ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent className="p-0">
          <div className="px-6">
            <p className="text-sm text-[var(--q3-neutral-light)] mb-4">{description}</p>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              {lines.map((line) => (
                <div key={line.dataKey} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: line.color }} />
                  <span className="text-sm text-[var(--q3-neutral-default)]">{line.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-60 w-full overflow-x-auto">
            <div style={{ width: '100%', minWidth: '600px', height: '100%' }}>
              <ChartContainer config={chartConfig} className="h-full w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={processedData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    onMouseMove={(state) => {
                      if (state && state.activeTooltipIndex !== undefined) {
                        setHoveredIndex(state.activeTooltipIndex);
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                      setHoveredLine(null);
                    }}
                  >
                    <CartesianGrid stroke="var(--q3-stroke-normal)" horizontal={true} vertical={true} />
                    <XAxis
                      dataKey={xAxisDataKey}
                      axisLine={false}
                      tickLine={false}
                      height={80}
                      interval={0}
                      tick={(props) => (
                        <CustomXAxisTick 
                          {...props} 
                          hoveredIndex={hoveredIndex} 
                          index={props.index} 
                          data={processedData}
                        />
                      )}
                    />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }} />
                    <ChartTooltip content={<CustomTooltipContent config={chartConfig} />} />
                    {lines.map((line) => (
                      <Line
                        isAnimationActive={false}
                        key={line.dataKey}
                        type="monotone"
                        dataKey={line.dataKey}
                        stroke={line.color}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{
                          fill: line.color,
                          strokeWidth: 2,
                          r: 4,
                          style: { display: hoveredLine === line.dataKey || hoveredLine === null ? 'block' : 'none' },
                        }}
                        onMouseEnter={() => setHoveredLine(line.dataKey)}
                        onMouseLeave={() => setHoveredLine(null)}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default MistakesLineChart;